import { connectInternalApi } from 'common/api'
import { Account, AccountResponse } from 'common/interfaces/accounts'

import { DashboardMode } from './Accounts'

export type PrivacyRegion = 'US' | 'EU' | 'UK'

export type PrivacyRegions = PrivacyRegion[]

export const mapAccountResponseToAccount = (
  account: AccountResponse
): Account => ({
  id: account._id,
  companyName: account.name,
  usersCount: account.users_count,
  campaignsCount: account.campaigns_count,
  projectsCount: account.projects_count,
  logoUrl: account.logo_url,
  industry: account.industry,
  accountCategory: account.account_category,
  dashboardMode: account.dashboardMode,
  status: account.deleted
    ? 'deactivated'
    : account.archived
      ? 'archived'
      : 'active',
  privacyRegions: account.privacy_regions,
  ...(account.umv_limit ? { umvLimit: account.umv_limit } : {}),
  ...(account.contract_start_date
    ? { contractStartDate: account.contract_start_date }
    : {}),
  ...(account.umv_used ? { umvUsed: account.umv_used } : {}),
  ...(account.brand_name !== undefined
    ? { brandName: account.brand_name }
    : {}),
  ...(account.brand_description !== undefined
    ? { brandDescription: account.brand_description }
    : {}),
})

const mapAccountToAccountResponse = (
  account: Partial<Account>
): Partial<AccountResponse> => ({
  name: account.companyName,
  industry: account.industry,
  deleted: account.status === 'deactivated',
  archived: account.status === 'archived',
})

export const getAccountsList = async (userId: string): Promise<Account[]> => {
  const response = await connectInternalApi.get<AccountResponse[]>(
    `v1/core/monorail/users/${userId}/accounts/reports/admin-account-list/`
  )
  return response.data.map(mapAccountResponseToAccount)
}

const updateAccountStatus = async (
  accountId: string,
  data: Partial<Account>
) => {
  const response = await connectInternalApi.patch(
    `v1/core/monorail/accounts/${accountId}`,
    mapAccountToAccountResponse(data)
  )

  return mapAccountResponseToAccount(response.data)
}

const createOrUpdateAccountWithLogo = async ({
  accountId,
  accountName,
  industry,
  accountCategory,
  dashboardMode,
  logo,
  privacyRegions,
  brandDescription,
  brandName,
}: {
  accountId?: string
  accountName: string
  industry: string
  accountCategory: string
  dashboardMode: DashboardMode
  logo: File
  privacyRegions: PrivacyRegions
  brandDescription?: string
  brandName?: string
}) => {
  const formData = new FormData()
  formData.append('name', accountName)
  formData.append('industry', industry)
  formData.append('account_category', accountCategory)
  formData.append('dashboardMode', dashboardMode)
  formData.append('file', logo)
  formData.append('privacy_regions', JSON.stringify(privacyRegions))
  formData.append('brandDescription', brandDescription || '')
  formData.append('brandName', brandName || '')

  // TODO: Fix it, this will be overwritten by the interceptor
  const headers = {
    'Content-Type': 'multipart/form-data',
  }

  const response = accountId
    ? await connectInternalApi.patch(
        `v1/core/monorail/accounts/${accountId}`,
        formData,
        {
          headers,
        }
      )
    : await connectInternalApi.post(`v1/core/monorail/accounts/`, formData, {
        headers,
      })

  return mapAccountResponseToAccount(response.data.data)
}

const createOrUpdateAccountWithoutLogo = async ({
  accountId,
  accountName,
  industry,
  accountCategory,
  dashboardMode,
  privacyRegions,
  contractStartDate,
  umvLimit,
  brandName,
  brandDescription,
}: {
  accountId?: string
  accountName: string
  industry: string
  accountCategory: string
  dashboardMode: DashboardMode
  privacyRegions: PrivacyRegions
  contractStartDate?: string
  umvLimit?: number
  brandName?: string
  brandDescription?: string
}) => {
  const data = {
    name: accountName,
    industry: industry,
    account_category: accountCategory,
    dashboardMode: dashboardMode,
    privacy_regions: privacyRegions,
    ...(contractStartDate
      ? { contract_start_date: new Date(contractStartDate).toISOString() }
      : {}),
    ...(umvLimit ? { umv_limit: umvLimit } : {}),
    ...(brandName !== undefined ? { brand_name: brandName } : {}),
    ...(brandDescription !== undefined
      ? { brand_description: brandDescription }
      : {}),
  }

  const response = accountId
    ? await connectInternalApi.patch(
        `v1/core/monorail/accounts/${accountId}`,
        data
      )
    : await connectInternalApi.post(`v1/core/monorail/accounts/`, data)
  return mapAccountResponseToAccount(response.data.data)
}

const addUserToAccount = async ({
  accountId,
  userId,
}: {
  accountId?: string
  userId?: string
}) => {
  const response = await connectInternalApi.post(
    `v1/core/monorail/users/${userId}/accounts/${accountId}/`
  )
  return response.data
}

const removeUserFromAccount = async ({
  accountId,
  userId,
}: {
  accountId?: string
  userId?: string
}) => {
  await connectInternalApi.delete(
    `v1/core/monorail/users/${userId}/accounts/${accountId}/`
  )
}

const api = {
  getAccountsList,
  createOrUpdateAccountWithLogo,
  createOrUpdateAccountWithoutLogo,
  updateAccountStatus,
  addUserToAccount,
  removeUserFromAccount,
}

export default api
